import React, { useState } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { X, Loader2, PhoneIncoming } from 'lucide-react';

const FONT_H = { fontFamily: '"Cabinet Grotesk", system-ui, sans-serif' };
const FONT_B = { fontFamily: 'Manrope, system-ui, sans-serif' };

const EMPTY = { client_name: '', client_phone: '', client_email: '', event_date: '', event_time: '', event_type: '', event_location: '', guest_count: '', estimated_total: '', notes: '' };

const Input = ({ label, field, form, setForm, type = 'text', placeholder = '' }) => (
  <div>
    <span className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold block mb-1.5" style={FONT_B}>{label}</span>
    <input type={type} value={form[field]} placeholder={placeholder}
           onChange={(e) => setForm(f => ({ ...f, [field]: e.target.value }))}
           className="w-full bg-white/[0.04] border border-white/10 text-white text-sm px-3.5 py-3 rounded-xl focus:outline-none focus:border-white/30 placeholder:text-zinc-700"
           style={FONT_B}
           data-testid={`quick-add-${field.replace(/_/g, '-')}`} />
  </div>
);

export const QuickAddLead = ({ onClose, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.client_name.trim()) { toast.error('Contact name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        client_name: form.client_name.trim(),
        guest_count: form.guest_count ? parseInt(form.guest_count) : null,
        estimated_total: form.estimated_total ? parseFloat(form.estimated_total) : 0,
      };
      const res = await api.post('/api/admin/leads/manual', payload);
      toast.success('Lead added to pipeline');
      onCreated(res.proposal_id);
    } catch (e) {
      toast.error(e.message || 'Failed to add lead');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm overflow-y-auto" data-testid="quick-add-modal">
      <div className="max-w-lg mx-auto min-h-screen bg-[#050505] border-x border-white/5 px-6 py-6">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h2 className="text-white text-[26px] font-black tracking-tighter flex items-center gap-3" style={FONT_H}>
              <PhoneIncoming className="w-5 h-5 text-zinc-400" /> New Lead
            </h2>
            <p className="text-zinc-500 text-xs mt-1.5" style={FONT_B}>Log a phone-in inquiry straight into the pipeline.</p>
          </div>
          <button onClick={onClose} className="w-11 h-11 flex items-center justify-center rounded-full bg-white/5 text-zinc-300 active:scale-95 transition-transform" data-testid="quick-add-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <Input label="Contact Name *" field="client_name" form={form} setForm={setForm} placeholder="Who called?" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Phone" field="client_phone" form={form} setForm={setForm} type="tel" placeholder="(305) …" />
            <Input label="Email" field="client_email" form={form} setForm={setForm} type="email" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Event Date" field="event_date" form={form} setForm={setForm} type="date" />
            <Input label="Time" field="event_time" form={form} setForm={setForm} placeholder="2:00 PM" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Event Type" field="event_type" form={form} setForm={setForm} placeholder="Birthday, Corporate…" />
            <Input label="Guests" field="guest_count" form={form} setForm={setForm} type="number" />
          </div>
          <Input label="Location" field="event_location" form={form} setForm={setForm} placeholder="Address or venue" />
          <Input label="Estimated Value ($)" field="estimated_total" form={form} setForm={setForm} type="number" placeholder="0" />
          <div>
            <span className="text-zinc-600 text-[10px] uppercase tracking-[0.2em] font-semibold block mb-1.5" style={FONT_B}>What did they ask about?</span>
            <textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} rows={3}
                      placeholder="Animals wanted, budget, anything discussed on the call…"
                      className="w-full bg-white/[0.04] border border-white/10 text-white text-sm px-3.5 py-3 rounded-xl focus:outline-none focus:border-white/30 resize-none placeholder:text-zinc-700"
                      style={FONT_B}
                      data-testid="quick-add-notes" />
          </div>
        </div>

        <div className="flex gap-3 mt-8 pb-10">
          <button onClick={submit} disabled={saving}
                  className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-black text-sm font-bold active:scale-95 disabled:opacity-50 transition-transform" style={FONT_B} data-testid="quick-add-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Add to Pipeline
          </button>
          <button onClick={onClose} className="px-7 py-3.5 rounded-full bg-white/5 border border-white/10 text-zinc-400 text-sm font-bold active:scale-95 transition-transform" style={FONT_B} data-testid="quick-add-cancel">Cancel</button>
        </div>
      </div>
    </div>
  );
};
